import { createSlice, PayloadAction, Draft } from "@reduxjs/toolkit";
import { ReceiptResponseDto } from "../../shared/dtos/receipt/ReceiptResponseDto";
import { ReceiptSummaryDto } from "../../shared/dtos/receipt/ReceiptSummaryDto";
import { SaleType } from "../../shared/enums/SaleType";
import {
  getInitialCrudState,
  generateCrudReducers,
} from "../utils/crudFactory";
import { InvoiceCartItem } from "./invoice";

interface PosInitialState {
  cartItems: InvoiceCartItem[];
  selectedCustomer: Record<string, unknown> | null;
  saleType: SaleType;
  cartSubtotal: number;
}

const calculateSubtotal = (items: InvoiceCartItem[]): number =>
  items.reduce(
    (acc, item) =>
      acc +
      (Number(item.price) - Number(item.discount || 0)) * Number(item.qty),
    0,
  );

const loadInitialState = (): PosInitialState => {
  try {
    const items = JSON.parse(localStorage.getItem("pos_items") || "[]");
    const customer = JSON.parse(localStorage.getItem("pos_customer") || "null");
    const saleType =
      (localStorage.getItem("pos_saleType") as SaleType) || SaleType.RETAIL;
    return {
      cartItems: items as InvoiceCartItem[],
      selectedCustomer: customer as Record<string, unknown> | null,
      saleType,
      cartSubtotal: calculateSubtotal(items),
    };
  } catch (e) {
    return {
      cartItems: [],
      selectedCustomer: null,
      saleType: SaleType.RETAIL,
      cartSubtotal: 0,
    };
  }
};

const initialState = {
  ...getInitialCrudState<ReceiptResponseDto, ReceiptSummaryDto>(),
  ...loadInitialState(),

  loadingPdf: false,
  loadingEmail: false,

  errorPdf: null as string | null,
  errorEmail: null as string | null,

  pdfUrl: null as string | null,
};

export const receiptSlice = createSlice({
  name: "receipt",
  initialState,
  reducers: {
    ...generateCrudReducers<ReceiptResponseDto, ReceiptSummaryDto>(),

    setLoadingPdf(state) {
      state.loadingPdf = true;
      state.errorPdf = null;
    },
    setLoadingEmail(state) {
      state.loadingEmail = true;
      state.errorEmail = null;
    },

    setErrorPdf(state, action: PayloadAction<string | null>) {
      state.loadingPdf = false;
      state.errorPdf = action.payload;
    },
    setErrorEmail(state, action: PayloadAction<string | null>) {
      state.loadingEmail = false;
      state.errorEmail = action.payload;
    },

    setPdfUrl(state, action: PayloadAction<string>) {
      state.loadingPdf = false;
      state.pdfUrl = action.payload;
    },
    clearPdfUrl(state) {
      if (state.pdfUrl) URL.revokeObjectURL(state.pdfUrl);
      state.pdfUrl = null;
    },
    setEmailSuccess(state) {
      state.loadingEmail = false;
    },

    // --- POS Cart Reducers ---
    setPosSaleType(state, action: PayloadAction<SaleType>) {
      state.saleType = action.payload;
      localStorage.setItem("pos_saleType", action.payload);
    },
    setPosCustomer(
      state,
      action: PayloadAction<Record<string, unknown> | null>,
    ) {
      state.selectedCustomer = action.payload as Draft<
        Record<string, unknown>
      > | null;
      localStorage.setItem("pos_customer", JSON.stringify(action.payload));
    },
    posItemAdd(state, action: PayloadAction<InvoiceCartItem>) {
      const existing = state.cartItems.find(
        (i) => i.itemId === action.payload.itemId,
      );
      if (existing) {
        existing.qty += action.payload.qty;
      } else {
        state.cartItems.push(action.payload as Draft<InvoiceCartItem>);
      }
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("pos_items", JSON.stringify(state.cartItems));
    },
    posItemUpdateFields(
      state,
      action: PayloadAction<{
        itemId: string;
        updates: Partial<InvoiceCartItem>;
      }>,
    ) {
      const item = state.cartItems.find(
        (i) => i.itemId === action.payload.itemId,
      );
      if (item) {
        if (action.payload.updates.qty !== undefined)
          item.qty = Number(action.payload.updates.qty);
        if (action.payload.updates.price !== undefined)
          item.price = Number(action.payload.updates.price);
        if (action.payload.updates.discount !== undefined)
          item.discount = Number(action.payload.updates.discount);
        if (item.qty <= 0)
          state.cartItems = state.cartItems.filter(
            (i) => i.itemId !== action.payload.itemId,
          );
      }
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("pos_items", JSON.stringify(state.cartItems));
    },
    posItemRemove(state, action: PayloadAction<string>) {
      state.cartItems = state.cartItems.filter(
        (i) => i.itemId !== action.payload,
      );
      state.cartSubtotal = calculateSubtotal(
        state.cartItems as InvoiceCartItem[],
      );
      localStorage.setItem("pos_items", JSON.stringify(state.cartItems));
    },
    clearPosCart(state) {
      state.cartItems = [];
      state.cartSubtotal = 0;
      state.selectedCustomer = null;
      localStorage.removeItem("pos_items");
      localStorage.removeItem("pos_customer");
    },
  },
});

export const receiptActions = receiptSlice.actions;
export default receiptSlice.reducer;
